import { API_URL } from "../../config";

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR INTELLIGENCE — API SERVICE & TYPES
   Admin-side API for vendor scoring, profit dashboard, pricing suggestions
   ═══════════════════════════════════════════════════════════════════════════ */

const hdrs = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...hdrs(), ...(options.headers as Record<string, string> || {}) },
  });
  const json = await res.json();
  if (!res.ok) throw { status: res.status, ...json };
  return json.data;
}

/* ── Types ──────────────────────────────────────────────────────────────── */

export interface ScoreBreakdown {
  cost_efficiency: number;
  profit_margin: number;
  reliability: number;
  usage_frequency: number;
  admin_rating: number;
}

export interface VendorRanking {
  vendor_id: number;
  vendor_name: string;
  vendor_price: number;
  dcc_price: number;
  estimated_profit: number;
  margin_percent: number;
  total_score: number;
  score_breakdown: ScoreBreakdown;
  badges: string[];
  usage_count: number;
  reliability_rate: number;
  admin_rating: number;
  is_override_preferred: boolean;
}

export interface VendorRecommendation {
  recommended: VendorRanking | null;
  rankings: VendorRanking[];
  explanation: string;
  has_override: boolean;
  item: { id: number; name: string };
}

export interface DashboardMetrics {
  total_vendor_spend: number;
  total_vendor_revenue: number;
  total_profit: number;
  avg_margin_percent: number;
  total_items_used: number;
  most_profitable_vendor: { id: number; name: string; profit: number } | null;
  most_profitable_service: { name: string; profit: number } | null;
  lowest_margin_vendor: { id: number; name: string; margin: number } | null;
}

export interface ChartDataPoint {
  vendor_id?: number;
  vendor_name?: string;
  service_name?: string;
  revenue: number;
  cost: number;
  profit: number;
  margin?: number;
}

export interface TrendData {
  labels: string[];
  revenue: number[];
  cost: number[];
  profit: number[];
}

export interface TopItem {
  item_id: number;
  item_name: string;
  vendor_name: string;
  service_name: string;
  pricing_type: string;
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
  times_used: number;
}

export interface DashboardData {
  metrics: DashboardMetrics;
  profit_by_vendor: ChartDataPoint[];
  profit_by_service: ChartDataPoint[];
  revenue_cost_trend: TrendData;
  margin_by_vendor: ChartDataPoint[];
  top_profitable_items: TopItem[];
}

export interface VendorPerformanceRow {
  vendor_id: number;
  vendor_name: string;
  services: string;
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
  jobs_count: number;
}

export interface ItemProfitabilityRow {
  item_id: number;
  item_name: string;
  vendor_id: number;
  vendor_name: string;
  service_name: string;
  pricing_type: string;
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
  times_used: number;
}

export interface PriceSuggestion {
  suggested_price: number;
  min_seen_price: number;
  max_seen_price: number;
  avg_price: number;
  confidence: "high" | "medium" | "low" | "none";
  reason: string;
  sample_size: number;
  avg_margin: number;
}

export interface ItemIntelligence {
  total_uses: number;
  avg_cost: number;
  avg_selling_price: number;
  avg_margin: number;
  avg_rate: number;
  min_rate: number;
  max_rate: number;
  avg_guest_count: number;
  last_used: string | null;
  most_common_pricing: string | null;
  most_common_event: string | null;
  pricing_type_breakdown: { type: string; count: number }[];
  event_type_breakdown: { type: string; count: number }[];
}

export interface VendorInsight {
  type: "positive" | "warning" | "info" | "suggestion" | "danger";
  message: string;
}

export interface VendorProfileIntelligence {
  summary: {
    services_provided: number;
    avg_margin: number;
    total_jobs: number;
    total_revenue: number;
    total_profit: number;
    recommendation_score: number;
    best_category: string | null;
    admin_rating: number;
  };
  top_items: { name: string; profit: number; count: number }[];
  recent_events: {
    item_name: string;
    event_type: string;
    event_date: string;
    revenue: number;
    cost: number;
    profit: number;
    margin: number;
  }[];
  insights: VendorInsight[];
}

export interface ItemAlert {
  type: "danger" | "warning" | "info";
  message: string;
}

export interface FilterOptions {
  event_types: string[];
  services: string[];
  vendors: { id: number; name: string }[];
  pricing_types: string[];
}

export interface DashboardFilters {
  date_from?: string;
  date_to?: string;
  event_type?: string;
  vendor_id?: number;
  service_name?: string;
  pricing_type?: string;
}

/* ── API Calls ──────────────────────────────────────────────────────────── */

const qs = (filters: Record<string, any>) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  });
  const s = params.toString();
  return s ? `?${s}` : "";
};

// Dashboard
export const fetchDashboard = (filters: DashboardFilters = {}): Promise<DashboardData> =>
  apiFetch(`/vendor-intelligence/dashboard${qs(filters)}`);

export const fetchFilterOptions = (): Promise<FilterOptions> =>
  apiFetch("/vendor-intelligence/filter-options");

export const fetchVendorPerformance = (filters: DashboardFilters = {}): Promise<VendorPerformanceRow[]> =>
  apiFetch(`/vendor-intelligence/vendor-performance${qs(filters)}`);

export const fetchItemProfitability = (filters: DashboardFilters = {}): Promise<ItemProfitabilityRow[]> =>
  apiFetch(`/vendor-intelligence/item-profitability${qs(filters)}`);

// Recommendation
export const fetchRecommendation = (itemId: number, context?: Record<string, any>): Promise<VendorRecommendation> =>
  apiFetch(`/vendor-intelligence/recommend/${itemId}${context ? qs(context) : ""}`);

export const fetchCategoryRecommendations = (categoryId: number): Promise<VendorRecommendation[]> =>
  apiFetch(`/vendor-intelligence/recommend-category/${categoryId}`);

export const postOverride = (data: {
  item_id: number;
  chosen_vendor_id: number;
  recommended_vendor_id?: number;
  override_reason?: string;
  remember_preference?: boolean;
}) => apiFetch("/vendor-intelligence/override", { method: "POST", body: JSON.stringify(data) });

export const postRateVendor = (data: { vendor_id: number; rating: number; notes?: string }) =>
  apiFetch("/vendor-intelligence/rate-vendor", { method: "POST", body: JSON.stringify(data) });

// Pricing suggestions
export const fetchSuggestedPrice = (params: {
  item_id?: number;
  vendor_id?: number;
  event_type?: string;
  guest_count?: number;
  pricing_type?: string;
  duration_hours?: number;
}): Promise<PriceSuggestion> =>
  apiFetch("/vendor-intelligence/suggest-price", { method: "POST", body: JSON.stringify(params) });

// Item intelligence
export const fetchItemIntelligence = (itemId: number, vendorId?: number): Promise<ItemIntelligence> =>
  apiFetch(`/vendor-intelligence/item-intelligence/${itemId}${vendorId ? `?vendor_id=${vendorId}` : ""}`);

export const fetchItemAlerts = (itemId: number, vendorId: number, proposedPrice?: number): Promise<ItemAlert[]> =>
  apiFetch(`/vendor-intelligence/item-alerts/${itemId}/${vendorId}${proposedPrice ? `?proposed_price=${proposedPrice}` : ""}`);

// Vendor profile intelligence
export const fetchVendorProfileIntelligence = (vendorId: number): Promise<VendorProfileIntelligence> =>
  apiFetch(`/vendor-intelligence/vendor-profile/${vendorId}`);

export const fetchVendorInsights = (vendorId: number): Promise<VendorInsight[]> =>
  apiFetch(`/vendor-intelligence/vendor-insights/${vendorId}`);

// Service intelligence
export const fetchServiceIntelligence = (serviceId: number): Promise<any[]> =>
  apiFetch(`/vendor-intelligence/service-intelligence/${serviceId}`);
